import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import FormLibraryRepository from '@/repositories/FormLibraryRepository';
import { Input, Modal } from '@/ui';
import rejectionMessage from '@/utils/rejectionMessage';

import fieldToProperty from '../schema/fieldToProperty';
import slugify from '../schema/slugify';
import type { FieldDef } from '../types';

import { fieldTypeMeta } from './fieldTypes';

interface SaveSectionDialogProps {
  sectionTitle: string;
  fields: FieldDef[];
  onClose: () => void;
  // Reports the new section id and each member's (possibly newly-minted)
  // question id so the canvas section can become linked.
  onSaved: (sectionId: string, questionIdsByFieldKey: Record<string, string>) => void;
}

const SaveSectionDialog = ({ sectionTitle, fields, onClose, onSaved }: SaveSectionDialogProps) => {
  const repo = FormLibraryRepository();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(sectionTitle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await write();
    } catch (failure) {
      // The repository rejects rather than returning on a 4xx — a member whose
      // key is already taken in the library is the everyday case — so without
      // this the dialog would hang on its spinner with no reason on screen.
      setError(rejectionMessage(failure, 'Failed to save section.'));
    } finally {
      setSaving(false);
    }
  };

  const write = async () => {
    const questionIds: Record<string, string> = {};

    // Section members must be library questions, so mint one for any member
    // that isn't shared yet before recording the section's membership. A field
    // linked to the form's own private question needs one too — its questionId
    // names a question the library picker never shows.
    for (const field of fields) {
      if (field.inLibrary && field.questionId) {
        questionIds[field.key] = field.questionId;
        continue;
      }
      const definition = { ...fieldToProperty(field) };
      delete definition['x-ui'].questionId;
      delete definition['x-ui'].inLibrary;
      const created = await repo.createQuestion({
        key: slugify(field.key, '') || slugify(field.label, 'question'),
        text: field.label || field.key,
        definition
      });
      if (created.isError || !created.data) {
        setError(created.errorMessage ?? 'Failed to save a question.');
        return;
      }
      questionIds[field.key] = created.data.id;
    }

    const section = await repo.createSection({
      key: slugify(title, 'section'),
      title,
      questions: fields.map((field) => ({
        questionId: questionIds[field.key],
        required: field.required
      }))
    });
    if (section.isError || !section.data) {
      setError(section.errorMessage ?? 'Failed to save section.');
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['librarySections'] });
    await queryClient.invalidateQueries({ queryKey: ['libraryQuestions'] });
    onSaved(section.data.id, questionIds);
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Save section to Section Library"
      description="Bundle these fields so the whole group can be inserted at once."
      size="md"
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
      primaryAction={{ label: 'Save section', onClick: save, isLoading: saving }}>
      <div className="flex flex-col gap-3.5">
        <Input label="Section name" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div>
          <div className="mb-1.5 text-[12.5px] font-semibold text-uniform-gray-700 dark:text-uniform-gray-300">
            Includes {fields.length} question{fields.length === 1 ? '' : 's'}
          </div>
          <div className="overflow-hidden rounded-lg border border-uniform-gray-200 dark:border-uniform-gray-700">
            {fields.map((field, index) => (
              <div
                key={field.key}
                className={`flex items-center gap-2 px-3 py-2.5 ${
                  index < fields.length - 1
                    ? 'border-b border-uniform-gray-100 dark:border-uniform-gray-700'
                    : ''
                }`}>
                <span className="rounded bg-uniform-gray-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-300">
                  {fieldTypeMeta(field.widget).badge}
                </span>
                <span className="text-[13px] text-uniform-gray-800 dark:text-uniform-gray-200">
                  {field.label || field.key}
                </span>
                {field.required && (
                  <span className="text-[12px] font-bold text-red-500 dark:text-red-400">*</span>
                )}
              </div>
            ))}
          </div>
        </div>
        <p className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
          <FontAwesomeIcon icon={faLayerGroup} className="mt-0.5 flex-none" />
          <span>
            This section stays <b>linked</b> to the library. Editing the master updates every form
            that uses it, unless a form unlinks its copy.
          </span>
        </p>
        {error && <p className="text-[12.5px] text-red-500 dark:text-red-400">{error}</p>}
      </div>
    </Modal>
  );
};

export default SaveSectionDialog;
